import { createRouter, createWebHashHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import type { App } from 'vue'
import { Layout } from '@/utils/routerHelper'
import { useI18n } from '@/hooks/web/useI18n'
import { NO_RESET_WHITE_LIST } from '@/constants'

const { t } = useI18n()

export const constantRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/',
    component: Layout,
    redirect: '/dashboard/analysis',
    name: 'Root',
    meta: {
      hidden: true
    }
  },
  {
    path: '/redirect',
    component: Layout,
    name: 'RedirectWrap',
    children: [
      {
        path: '/redirect/:path(.*)',
        name: 'Redirect',
        component: () => import('@/views/Redirect/Redirect.vue'),
        meta: {}
      }
    ],
    meta: {
      hidden: true,
      noTagsView: true
    }
  },
  {
    path: '/login',
    component: () => import('@/views/Login/Login.vue'),
    name: 'Login',
    meta: {
      hidden: true,
      title: '登录',
      noTagsView: true
    }
  },
  {
    path: '/personal',
    component: Layout,
    redirect: '/personal/personal-center',
    name: 'Personal',
    meta: {
      title: '个人',
      hidden: true,
      canTo: true
    },
    children: [
      {
        path: 'personal-center',
        component: () => import('@/views/Personal/PersonalCenter/PersonalCenter.vue'),
        name: 'PersonalCenter',
        meta: {
          title: '个人中心',
          hidden: true,
          canTo: true
        }
      }
    ]
  },
  {
    path: '/403',
    component: () => import('@/views/Error/403.vue'),
    name: 'NoFind403',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  },
  {
    path: '/404',
    component: () => import('@/views/Error/404.vue'),
    name: 'NoFind404',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  },
  {
    path: '/500',
    component: () => import('@/views/Error/500.vue'),
    name: 'NoFind500',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  }
]

// 前端控制的动态路由: 若后端没有返回路由，则使用此静态路由
export const asyncRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/dashboard',
    component: Layout,
    redirect: '/dashboard/analysis',
    name: 'Dashboard',
    meta: {
      title: '首页',
      icon: 'vi-ant-design:dashboard-filled',
      alwaysShow: true
    },
    children: [
      {
        path: 'analysis',
        component: () => import('@/views/Dashboard/Analysis/Analysis.vue'),
        name: 'Analysis',
        meta: {
          title: '分析页'
          // noCache: true,
          // affix: true
        }
      },
      {
        path: 'web-body',
        component: () => import('@/views/Dashboard/WebBody/WebBody.vue'),
        name: 'WebBody',
        meta: {
          title: '网体架构'
        }
      },
      {
        path: 'revenue-ranking',
        component: () => import('@/views/Dashboard/RevenueRanking/RevenueRanking.vue'),
        name: 'RevenueRanking',
        meta: {
          title: '收益排名'
        }
      },
      {
        path: 'level-ranking',
        component: () => import('@/views/Dashboard/LevelRanking/LevelRanking.vue'),
        name: 'LevelRanking',
        meta: {
          title: '等级排名'
        }
      },
      {
        path: 'team-statistics',
        component: () => import('@/views/Dashboard/TeamStatistics/TeamStatistics.vue'),
        name: 'TeamStatistics',
        meta: {
          title: '用户团队统计'
        }
      },
      {
        path: 'node-partner',
        component: () => import('@/views/Dashboard/NodePartner/NodePartner.vue'),
        name: 'NodePartner',
        meta: {
          title: '节点合伙人列表'
        }
      },
      {
        path: 'community-ranking',
        component: () => import('@/views/Dashboard/CommunityRanking/CommunityRanking.vue'),
        name: 'CommunityRanking',
        meta: {
          title: '小区业绩排行'
        }
      }
    ]
  },
  {
    path: '/users',
    component: Layout,
    redirect: '/users/list',
    name: 'Users',
    meta: {
      title: '用户管理',
      icon: 'material-symbols:manage-accounts',
      alwaysShow: true
    },
    children: [
      {
        path: 'list',
        component: () => import('@/views/Users/List/List.vue'),
        name: 'UserList',
        meta: {
          title: '用户列表'
        }
      },
      {
        path: 'certification',
        component: () => import('@/views/Users/Certification/Certification.vue'),
        name: 'UserCertification',
        meta: {
          title: '实名认证审核'
        }
      },
      {
        path: 'partner',
        component: () => import('@/views/Users/Partner/Partner.vue'),
        name: 'UserPartner',
        meta: {
          title: '合伙人等级'
        }
      },
      {
        path: 'vip',
        component: () => import('@/views/Users/Vip/Vip.vue'),
        name: 'UserVIP',
        meta: {
          title: '会员等级'
        }
      },
      {
        path: 'invite',
        component: () => import('@/views/Users/Invite/Invite.vue'),
        name: 'UserInvite',
        meta: {
          title: '推荐关系列表'
        }
      },
      {
        path: 'login-log',
        component: () => import('@/views/Users/LoginLog/LoginLog.vue'),
        name: 'UserLoginLog',
        meta: {
          title: '登录日志'
        }
      },
      {
        path: 'ip-black-list',
        component: () => import('@/views/Users/IpBlackList/IpBlackList.vue'),
        name: 'IpBlackList',
        meta: {
          title: 'IP 黑名单'
        }
      },
      {
        path: 'address-black-list',
        component: () => import('@/views/Users/AddressBlackList/AddressBlackList.vue'),
        name: 'AddressBlackList',
        meta: {
          title: '地址黑名单'
        }
      },
      {
        path: 'poster',
        component: () => import('@/views/Users/Poster/Poster.vue'),
        name: 'Poster',
        meta: {
          title: '推广海报'
        }
      },
      {
        path: 'activation-code',
        component: () => import('@/views/Users/ActivationCode/ActivationCode.vue'),
        name: 'ActivationCode',
        meta: {
          title: '激活码管理'
        }
      }
    ]
  },
  {
    path: '/finance',
    component: Layout,
    redirect: '/finance/balance',
    name: 'Finance',
    meta: {
      title: '资金与资产管理',
      icon: 'ant-design:account-book-filled',
      alwaysShow: true
    },
    children: [
      {
        path: 'balance',
        component: () => import('@/views/Finance/Balance/Balance.vue'),
        name: 'FinanceBalance',
        meta: {
          title: '用户余额列表'
        }
      },
      {
        path: 'recharge-records',
        component: () => import('@/views/Finance/RechargeRecords/RechargeRecords.vue'),
        name: 'FinanceRechargeRecords',
        meta: {
          title: '充值记录'
        }
      },
      {
        path: 'withdrawal-records',
        component: () => import('@/views/Finance/WithdrawalRecords/WithdrawalRecords.vue'),
        name: 'FinanceWithdrawalRecords',
        meta: {
          title: '提现记录'
        }
      },
      {
        path: 'withdrawal-account',
        component: () => import('@/views/Finance/WithdrawalAccount/WithdrawalAccount.vue'),
        name: 'FinanceWithdrawalAccount',
        meta: {
          title: '提现账号列表'
        }
      },
      {
        path: 'fund-record',
        component: () => import('@/views/Finance/FundRecord/FundRecord.vue'),
        name: 'FundRecord',
        meta: {
          title: '会员资金记录'
        }
      },
      {
        path: 'wallet-address',
        component: () => import('@/views/Finance/WalletAddress/WalletAddress.vue'),
        name: 'WalletAddress',
        meta: {
          title: '用户钱包地址'
        }
      },
      {
        path: 'transfer',
        component: () => import('@/views/Finance/Transfer/Transfer.vue'),
        name: 'Transfer',
        meta: {
          title: '用户转账记录'
        }
      }
    ]
  },
  {
    path: '/computing-power',
    component: Layout,
    redirect: '/computing-power/products',
    name: 'ComputingPower',
    meta: {
      title: '产品与订单管理',
      icon: 'ant-design:cloud-twotone',
      alwaysShow: true
    },
    children: [
      {
        path: 'products',
        component: () => import('@/views/ComputingPower/Products/Products.vue'),
        name: 'ComputingPowerProducts',
        meta: {
          title: '产品管理'
        }
      },
      {
        path: 'orders',
        component: () => import('@/views/ComputingPower/Orders/Orders.vue'),
        name: 'ComputingPowerOrders',
        meta: {
          title: '订单管理'
        }
      },
      {
        path: 'hosting',
        component: () => import('@/views/ComputingPower/Hosting/Hosting.vue'),
        name: 'ComputingPowerHosting',
        meta: {
          title: '托管订单管理'
        }
      },
      {
        path: 'release',
        component: () => import('@/views/ComputingPower/Release/Release.vue'),
        name: 'ComputingPowerRelease',
        meta: {
          title: '订单释放'
        }
      },
      {
        path: 'release-log',
        component: () => import('@/views/ComputingPower/ReleaseLog/ReleaseLog.vue'),
        name: 'ComputingPowerReleaseLog',
        meta: {
          title: '收益释放记录'
        }
      },
      {
        path: 'server-config',
        component: () => import('@/views/ComputingPower/ServerConfig/ServerConfig.vue'),
        name: 'ComputingPowerServerConfig',
        meta: {
          title: '服务器配置'
        }
      },
      {
        path: 'nodes',
        component: () => import('@/views/ComputingPower/Nodes/Nodes.vue'),
        name: 'Nodes',
        meta: {
          title: '主机分布图'
        }
      }
    ]
  },
  {
    path: '/mall',
    component: Layout,
    redirect: '/mall/product-list',
    name: 'Mall',
    meta: {
      title: '商城管理',
      icon: 'ant-design:shopping-twotone',
      alwaysShow: true
    },
    children: [
      {
        path: 'product-list',
        component: () => import('@/views/Mall/ProductList/ProductList.vue'),
        name: 'MallProductList',
        meta: {
          title: '商品列表'
        }
      },
      {
        path: 'order-list',
        component: () => import('@/views/Mall/OrderList/OrderList.vue'),
        name: 'MallOrderList',
        meta: {
          title: '商品订单'
        }
      },
      {
        path: 'audit-product-list',
        component: () => import('@/views/Mall/AuditProductList/AuditProductList.vue'),
        name: 'AuditMallProductList',
        meta: {
          title: '审核商品列表'
        }
      },
      {
        path: 'audit-order-list',
        component: () => import('@/views/Mall/AuditOrderList/AuditOrderList.vue'),
        name: 'AuditMallOrderList',
        meta: {
          title: '审核商品订单'
        }
      }
    ]
  },
  {
    path: '/wallet',
    component: Layout,
    redirect: '/wallet/address-list',
    name: 'Wallet',
    meta: {
      title: '钱包管理',
      icon: 'material-symbols:account-balance-wallet',
      alwaysShow: true
    },
    children: [
      {
        path: 'address-list',
        component: () => import('@/views/Wallet/AddressList/AddressList.vue'),
        name: 'WalletAddressList',
        meta: {
          title: '用户钱包地址列表'
        }
      },
      {
        path: 'balance-list',
        component: () => import('@/views/Wallet/BalanceList/BalanceList.vue'),
        name: 'WalletBalance',
        meta: {
          title: '钱包余额列表'
        }
      },
      {
        path: 'transfer-record',
        component: () => import('@/views/Wallet/TransferRecord/TransferRecord.vue'),
        name: 'WalletTransferRecord',
        meta: {
          title: '钱包转账记录'
        }
      }
    ]
  },
  {
    path: '/chat',
    component: Layout,
    redirect: '/chat/group-list',
    name: 'Chat',
    meta: {
      title: 'IM管理',
      icon: 'vi-ant-design:robot-filled',
      alwaysShow: true
    },
    children: [
      {
        path: 'group-list',
        component: () => import('@/views/Chat/GroupList/GroupList.vue'),
        name: 'ChatGroupList',
        meta: {
          title: '群列表'
        }
      }
      // {
      //   path: 'group-member-list',
      //   component: () => import('@/views/Chat/GroupMemberList/GroupMemberList.vue'),
      //   name: 'ChatGroupMemberList',
      //   meta: {
      //     title: '群成员列表'
      //   }
      // },
      // {
      //   path: 'group-chat-records',
      //   component: () => import('@/views/Chat/GroupChatRecords/GroupChatRecords.vue'),
      //   name: 'ChatGroupChatRecords',
      //   meta: {
      //     title: '群聊天记录'
      //   }
      // },
      // {
      //   path: 'group-announcement',
      //   component: () => import('@/views/Chat/GroupAnnouncement/GroupAnnouncement.vue'),
      //   name: 'ChatGroupAnnouncement',
      //   meta: {
      //     title: '群公告'
      //   }
      // }
    ]
  },
  {
    path: '/messages',
    component: Layout,
    redirect: '/messages/announcements',
    name: 'Messages',
    meta: {
      title: '消息与通知',
      icon: 'vi-ant-design:message-filled',
      alwaysShow: true
    },
    children: [
      {
        path: 'announcements',
        component: () => import('@/views/Messages/Announcements/Announcements.vue'),
        name: 'MessagesAnnouncements',
        meta: {
          title: '文章公告管理'
        }
      }
    ]
  },
  {
    path: '/content',
    component: Layout,
    redirect: '/content/learning-zone',
    name: 'Content',
    meta: {
      title: '内容管理',
      icon: 'vi-ant-design:file-text-filled',
      alwaysShow: true
    },
    children: [
      {
        path: 'learning-zone',
        component: () => import('@/views/Content/LearningZone/LearningZone.vue'),
        name: 'ContentLearningZone',
        meta: {
          title: '学习中心'
        }
      },
      {
        path: 'category',
        component: () => import('@/views/Content/Category/Category.vue'),
        name: 'ContentCategory',
        meta: {
          title: '常见问题分类'
        }
      },
      {
        path: 'faq',
        component: () => import('@/views/Content/Faq/Faq.vue'),
        name: 'ContentFaq',
        meta: {
          title: '常见问题与答案'
        }
      }
    ]
  },
  {
    path: '/auth',
    component: Layout,
    redirect: '/auth/menu',
    name: 'Auth',
    meta: {
      title: '权限管理',
      icon: 'vi-eos-icons:role-binding',
      alwaysShow: true
    },
    children: [
      {
        path: 'menu',
        component: () => import('@/views/Auth/Menu/Menu.vue'),
        name: 'AuthMenu',
        meta: {
          title: '菜单管理',
          // 测试权限使用
          permission: ['add', 'edit', 'delete']
        }
      },
      {
        path: 'role',
        component: () => import('@/views/Auth/Role/Role.vue'),
        name: 'AuthRole',
        meta: {
          title: '角色管理'
        }
      }
    ]
  },
  {
    path: '/admin',
    component: Layout,
    redirect: '/admin/list',
    name: 'Admin',
    meta: {
      title: '管理员管理',
      icon: 'clarity:administrator-solid',
      alwaysShow: true
    },
    children: [
      {
        path: 'list',
        component: () => import('@/views/Admin/List/List.vue'),
        name: 'AdminList',
        meta: {
          title: '管理员列表'
        }
      },
      {
        path: 'admin-log',
        component: () => import('@/views/Admin/AdminLog/AdminLog.vue'),
        name: 'AdminAdminLog',
        meta: {
          title: '管理员登录日志'
        }
      },
      {
        path: 'operation',
        component: () => import('@/views/Admin/Operation/Operation.vue'),
        name: 'AdminOperation',
        meta: {
          title: '管理员操作日志'
        }
      }
    ]
  },
  {
    path: '/system',
    component: Layout,
    redirect: '/system/config',
    name: 'System',
    meta: {
      title: '系统管理',
      icon: 'vi-ant-design:setting-filled',
      alwaysShow: true
    },
    children: [
      {
        path: 'config',
        component: () => import('@/views/System/Config/Config.vue'),
        name: 'SystemConfig',
        meta: {
          title: '系统配置'
        }
      },
      {
        path: 'sms',
        component: () => import('@/views/System/Sms/Sms.vue'),
        name: 'SystemSms',
        meta: {
          title: '短信验证码管理'
        }
      },
      {
        path: 'rate',
        component: () => import('@/views/System/Rate/Rate.vue'),
        name: 'SystemRate',
        meta: {
          title: '汇率管理'
        }
      },
      {
        path: 'contracts',
        component: () => import('@/views/System/Contracts/Contracts.vue'),
        name: 'SystemContracts',
        meta: {
          title: '合同管理'
        }
      },
      {
        path: 'pay',
        component: () => import('@/views/System/Pay/Pay.vue'),
        name: 'SystemPay',
        meta: {
          title: '三方支付管理'
        }
      },
      {
        path: 'apk',
        component: () => import('@/views/System/Apk/Apk.vue'),
        name: 'SystemApk',
        meta: {
          title: '应用管理'
        }
      }
    ]
  },
  {
    path: '/error',
    component: Layout,
    redirect: '/error/404',
    name: 'Error',
    meta: {
      title: t('router.errorPage'),
      hidden: true
    },
    children: [
      {
        path: '404-demo',
        component: () => import('@/views/Error/404.vue'),
        name: '404Demo',
        meta: {
          title: '404'
        }
      },
      {
        path: '403-demo',
        component: () => import('@/views/Error/403.vue'),
        name: '403Demo',
        meta: {
          title: '403'
        }
      },
      {
        path: '500-demo',
        component: () => import('@/views/Error/500.vue'),
        name: '500Demo',
        meta: {
          title: '500'
        }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHashHistory(),
  strict: true,
  routes: constantRouterMap as RouteRecordRaw[],
  scrollBehavior: () => ({ left: 0, top: 0 })
})

export const resetRouter = (): void => {
  router.getRoutes().forEach((route) => {
    const { name } = route
    if (name && !NO_RESET_WHITE_LIST.includes(name as string)) {
      router.hasRoute(name) && router.removeRoute(name)
    }
  })
}

export const setupRouter = (app: App<Element>) => {
  app.use(router)
}

export default router
